import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Store, CheckCircle, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

const API = '/api';
const SOUND_URL = '/api/sounds/aguardando-aceite';
const STATE_POLL_MS = 15_000;          // estado universal: 15s
const SOUND_LOOP_INTERVAL_MS = 10_000; // toca o som a cada 10s enquanto popup aberto

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export default function OpenStorePrompt() {
  // Lista de aberturas do dia vinda do backend (estado universal)
  const [openings, setOpenings] = useState([]);
  const [windowBeforeMin, setWindowBeforeMin] = useState(30);
  const [confirming, setConfirming] = useState(false);
  const [externallyClosed, setExternallyClosed] = useState(false);
  const audioRef = useRef(null);
  const loopRef = useRef(null);
  // Flag de "usuário já interagiu" — browsers bloqueiam autoplay sem isso
  const userGestureRef = useRef(false);

  // Poll do estado universal (sumiço sincroniza entre aparelhos)
  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/store/opening-state`, getAuthHeader());
      setOpenings(Array.isArray(res.data?.openings) ? res.data.openings : []);
      setExternallyClosed(!!res.data?.manually_closed_externally);
      if (typeof res.data?.window_before_min === "number") {
        setWindowBeforeMin(res.data.window_before_min);
      }
    } catch {
      // sem auth ou backend off — fica inerte
    }
  }, []);

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, STATE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchState]);

  // Sincronização instantânea entre tabs do MESMO aparelho (não espera o poll)
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('nucleo_store_opening');
      bc.onmessage = (ev) => {
        if (ev?.data?.type === 'confirmed') fetchState();
      };
    } catch {}
    return () => { try { bc && bc.close(); } catch {} };
  }, [fetchState]);

  // Seleciona a abertura "ativa" de hoje: a primeira não confirmada que esteja na janela (30min antes)
  // OU já no horário/atrasada. Se a loja foi fechada manualmente pelo lojista (motivo diferente
  // do nosso auto-close), respeita a decisão e não mostra popup.
  const active = externallyClosed ? null : openings.find(o => {
    if (o.confirmed) return false;
    const mu = typeof o.minutes_until === 'number' ? o.minutes_until : null;
    if (mu === null) return false;
    if (mu > 0 && mu <= windowBeforeMin) return true; // janela antes
    if (mu <= 0) return true;                          // já bateu ou atrasou
    return false;
  });

  const popupOpen = !!active;

  // Browsers exigem gesto do usuário para tocar áudio. Em refresh sem interação,
  // a primeira play() falha silenciosamente. Aqui escutamos o primeiro clique/tecla
  // do documento e re-tentamos a tocar enquanto o popup estiver aberto.
  useEffect(() => {
    const armGesture = () => {
      userGestureRef.current = true;
      // Se o popup já está aberto mas o áudio falhou, força um playOnce na próxima iteração
      if (popupOpen && !audioRef.current) {
        try {
          const a = new Audio(SOUND_URL);
          a.volume = 1.0;
          audioRef.current = a;
          a.onended = () => { audioRef.current = null; };
          a.play().catch(() => { audioRef.current = null; });
        } catch {}
      }
    };
    window.addEventListener('pointerdown', armGesture);
    window.addEventListener('keydown', armGesture);
    window.addEventListener('touchstart', armGesture);
    return () => {
      window.removeEventListener('pointerdown', armGesture);
      window.removeEventListener('keydown', armGesture);
      window.removeEventListener('touchstart', armGesture);
    };
  }, [popupOpen]);

  // Loop sonoro enquanto popup aberto
  useEffect(() => {
    if (!popupOpen) return;
    const playOnce = () => {
      try {
        const a = new Audio(SOUND_URL);
        a.volume = 1.0;
        audioRef.current = a;
        a.onended = () => { audioRef.current = null; };
        a.play().catch(() => { audioRef.current = null; });
      } catch {
        audioRef.current = null;
      }
    };
    playOnce();
    loopRef.current = setInterval(() => {
      if (!audioRef.current) playOnce();
    }, SOUND_LOOP_INTERVAL_MS);
    return () => {
      if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
      if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    };
  }, [popupOpen]);

  const handleConfirm = async () => {
    if (!active || confirming) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/store/opening-confirm`, { opening_time: active.opening_time }, getAuthHeader());
      // refresh imediato pra esconder localmente + avisa outras tabs deste aparelho
      try {
        const bc = new BroadcastChannel('nucleo_store_opening');
        bc.postMessage({ type: 'confirmed', opening_time: active.opening_time });
        bc.close();
      } catch {}
      await fetchState();
    } catch (e) {
      console.error('[OpenStorePrompt] confirm error:', e);
    } finally {
      setConfirming(false);
    }
  };

  // Sinaliza globalmente que este popup crítico está aberto pra outros modais
  // (ex.: ReleaseNotesModal) esperarem fechar antes de abrir — evita sobreposição
  // que rouba clicks. Usa data-attribute no body (barato, sem context/eventos).
  useEffect(() => {
    if (popupOpen) {
      document.body.dataset.storePromptOpen = "true";
      return () => { delete document.body.dataset.storePromptOpen; };
    }
  }, [popupOpen]);

  if (!popupOpen) return null;

  const overdue = (active.minutes_overdue || 0) > 0;
  const overdueText = overdue
    ? (active.minutes_overdue >= 60
        ? `${Math.floor(active.minutes_overdue / 60)}h ${active.minutes_overdue % 60}min`
        : `${active.minutes_overdue} min`)
    : '';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className={`relative max-w-md w-full bg-card border-2 rounded-3xl shadow-2xl overflow-hidden ${
        overdue ? 'border-red-500 shadow-red-500/30' : 'border-orange-500 shadow-orange-500/30'
      }`}>
        <div className="p-7 text-center">
          <div className="relative w-24 h-24 mx-auto mb-5">
            <div className={`absolute inset-0 rounded-full opacity-25 animate-ping ${overdue ? 'bg-red-500' : 'bg-orange-500'}`} />
            <div className={`relative w-24 h-24 rounded-full text-white flex items-center justify-center shadow-xl ${
              overdue
                ? 'bg-gradient-to-br from-red-400 to-red-600 shadow-red-500/40'
                : 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-orange-500/40'
            }`}>
              {overdue ? <AlertTriangle className="w-12 h-12" strokeWidth={2} /> : <Store className="w-12 h-12" strokeWidth={2} />}
            </div>
          </div>

          {overdue ? (
            <>
              <h2 className="font-bold text-2xl mb-2 tracking-tight text-foreground">A loja está fechada</h2>
              <p className="text-base text-muted-foreground mb-1">
                Deveria abrir às <span className="font-bold text-red-500">{active.opening_time}</span>
              </p>
              <p className="text-sm text-red-500 font-semibold mb-1">
                Atraso: {overdueText}
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                A loja só abrirá após alguém clicar em ESTOU PRONTO.
                {active.alert_sent ? ' Aviso já foi enviado ao responsável pelo WhatsApp.' : ''}
              </p>
            </>
          ) : (
            <>
              <h2 className="font-bold text-2xl mb-2 tracking-tight text-foreground">A loja abre em breve</h2>
              <p className="text-base text-muted-foreground mb-1">Está pronto para vender hoje?</p>
              <p className="text-sm text-muted-foreground mb-6">
                Abertura prevista para <span className="font-bold text-orange-500">{active.opening_time}</span>
              </p>
            </>
          )}

          <Button
            onClick={handleConfirm}
            disabled={confirming}
            className={`w-full text-white font-bold text-base py-6 gap-2 shadow-lg ${
              overdue
                ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-red-500/30'
                : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-500/30'
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            {confirming ? 'CONFIRMANDO...' : 'ESTOU PRONTO'}
          </Button>
        </div>
      </div>
    </div>
  );
}
